<template>
  <div class="contact-conversation">
    <div class="contact-conversation__expand-trigger" @click="toggleMessageTray()">
      <div
        class="contact-conversation__trigger-content"
        :class="{ 'contact-conversation__trigger-content--expanded': messageTrayOpen }"
      >
        <div class="contact-conversation__platform">
          <ChannelIcon :channelType="channelType" />
        </div>
        <div class="contact-conversation__details">
          <h2 class="contact-conversation__title">
            {{ truncatedTitle }}
          </h2>
          <span class="contact-conversation__date">
            {{ conversationDate }}
          </span>
        </div>
        <div class="contact-conversation__header-right">
          <div
            class="contact-conversation__expanded-indicator"
            :class="{ 'contact-conversation__expanded-indicator--expanded': messageTrayOpen }"
          >
            <svg class="right-arrow">
              <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#arrow-right2" />
            </svg>
          </div>
        </div>
      </div>
    </div>
    <div class="contact-conversation__message-container" v-if="messageTrayOpen">
      <n-loading-spinner v-if="loading" />
      <n-conversation-thread
        :messages="messages"
        @load="onLoad"
        v-if="!loading"
        :displayAvatar="true"
        :enableAvatarTooltip="true"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import moment from 'moment';
import { Conversation } from '../models/conversation';
import { conversationApi } from '../services/conversationApi';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import { ConversationMessage } from '../models/conversation-message';
import ChannelIcon from '@/shared/icons/ChannelIcon.vue';
import { computed, onMounted, PropType, ref } from 'vue';

const props = defineProps({
  conversation: {
    type: Object as PropType<Conversation>,
    required: true,
  },
  workspaceId: {
    type: String,
    required: true,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
});

const maxConversationTitleLength = ref(50);
const messages = ref<any[]>([]);
const loading = ref(false);
const messageTrayOpen = ref(false);
const hasLoadedMessages = ref(false);

onMounted(() => {
  if (props.expanded) {
    toggleMessageTray();
  }
});

const truncatedTitle = computed(() => {
  if (!props.conversation.title) {
    return `Conversation on ${channelIdentityName.value}`;
  }
  return props.conversation.title.length > maxConversationTitleLength.value
    ? props.conversation.title.substring(0, maxConversationTitleLength.value) + '...'
    : props.conversation.title;
});

const conversationDate = computed(() => {
  const date = new Date(props.conversation.createdTimeUtc);
  return moment(date).format('dddd Do MMMM');
});

const channelType = computed(() => {
  return props.conversation.channelIdentity.type;
});

const channelIdentityName = computed(() => {
  switch (channelType.value) {
    case 'sms-bt':
      return 'BT Smart Messaging Tailored';
    case 'sms-govuknotify':
      return 'GOV.UK Notify SMS';
    case 'messenger':
      return 'Facebook Messenger';
    case 'instagram':
      return 'Instagram';
    case 'whatsapp':
      return 'WhatsApp';
    default:
      return 'Web Assistant';
  }
});

async function getMessages() {
  loading.value = true;
  const response = await conversationApi.getConversationMessages(props.workspaceId, props.conversation.id);
  messages.value = response.messages?.map((message) => ({
    type: getMessageType(message),
    text: getMessageText(message),
    direction: message.role === 'appUser' ? 'inbound' : 'outbound',
    status: message.status,
    timestampUtc: message.received,
    actions: message.actions ? message.actions.map((action) => action.payload) : [],
    name: !message.name ? 'Unknown' : message.name,
    avatarUrl: message.avatarUrl,
    card: getMessageCard(message),
    mediaSrc: message.mediaUrl,
    fileName: message.mediaFilename,
    fileSize: message.mediaSize,
    fileType: message.mediaType,
  }));
  loading.value = false;
}

function getMessageType(m: ConversationMessage): string {
  if (m.role === 'whisper') {
    return 'whisper';
  }
  return m.type;
}

function getMessageText(m: ConversationMessage): string {
  if (m.role === 'whisper' && m.text) {
    return m.text;
  }

  if (m.role === 'whisper' && m.metadata) {
    return m.metadata.description;
  }

  if (m.type === 'text') {
    return m.text;
  }

  return '';
}

function getMessageCard(m: ConversationMessage): any {
  if (m.type === 'app' && m.app) {
    return { content: m.app.card };
  }
  return null;
}

function toggleMessageTray() {
  if (shouldLoadMessages()) {
    getMessages();
  }
  messageTrayOpen.value = !messageTrayOpen.value;
}

function shouldLoadMessages(): boolean {
  if (messageTrayOpen.value) {
    return false;
  }
  return !hasLoadedMessages.value || props.conversation.status === 'open';
}

function onLoad($state: any) {
  // Mark state as complete to hide the loader.on the conversation thread
  $state.complete();
}
</script>

<style lang="scss" scoped>
.contact-conversation {
  border-radius: 6px;
  border: 1px solid #d3d3d3;
  background: #fff;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-height: 64px;
  &__expand-trigger {
    padding: 0 1rem;
    cursor: pointer;
  }
  &__trigger-content {
    display: flex;
    flex-wrap: wrap;
    padding: 0.5rem 0;
    &--expanded {
      border-bottom: 1px solid rgb(208, 209, 213, 0.5);
    }
  }
  &__platform {
    flex: 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.25rem;
    margin-right: 0.75rem;
    svg {
      max-width: 32px;
      max-height: 32px;
    }
  }
  &__details {
    flex: 1 0;
  }
  &__title {
    font-size: 1.125rem;
    line-height: 22px;
  }
  &__date {
    color: #212529;
    opacity: 0.8;
    font-size: 0.875rem;
  }
  &__message-container {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 0 0.5rem;
    min-height: fit-content;
    max-height: 400px;
  }
  &__header-right {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__expanded-indicator {
    .right-arrow {
      height: 1rem;
      width: 2rem;
      stroke: $primary;
      transition: transform 200ms;
      display: block;
    }
    &--expanded {
      .right-arrow {
        transform: rotate(90deg);
      }
    }
  }
  &__status-blob {
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
    background: red;
    display: block;
    &--open {
      background: green;
    }
  }
  .n-conversation-thread {
    overflow: hidden;
  }
}
</style>
